// src/api/bootloopClient.ts

export type ShoeType = 0 | 1 | 2 | 3 | 4;
export type BootCondition = 0 | 1 | 2 | 3 | 4;

export type CreateListingRequest = {
  email: string;
  postcode?: string | null;
  sizeUk: number;
  shoeType: ShoeType;
  condition: BootCondition;
  notes?: string | null;
  photoUrl?: string | null;
};

export type CreateInterestRequest = {
  email: string;
  postcode?: string | null;
};

export type BootListingDto = {
  listingId: number;
  ownerUserId: number;
  sizeUk: number;
  shoeType: ShoeType;
  condition: BootCondition;
  notes?: string | null;
  photoUrl?: string | null;
  status: number;
  createdUtc: string;
};

export type ListingInterestDto = {
  interestId: number;
  listingId: number;
  interestedUserId: number;
  status: number;
  createdUtc: string;
};

/** Match results return everything in BootListingDto + a computed distance */
export type BootListingMatchDto = BootListingDto & {
  distanceMiles?: number | null;
  ownerEmail?: string | null;
};

export type SwapPreferenceCreateRequest = {
  intent: "have" | "need" | "both";
  bootSizeUk: string; // keep as string, e.g. "2.5"
  postcode: string;
  source?: string | null;
};

export type SwapPreferenceCreateResponse = {
  id: number;
};

export type SwapSizeSummaryRow = {
  bootSizeUk: string;
  total: number;
  have: number;
  need: number;
  both: number;
};

export type SwapHotspotRow = {
  outwardCode: string;
  total: number;
  distinctPostcodes: number;
  lastSeenUtc: string;
};

export type SwapPreferencesSummaryResponse = {
  sizes: SwapSizeSummaryRow[];
  hotspots: SwapHotspotRow[];
};

export type FindMatchesArgs = {
  postcode: string;
  sizeUk: number;
  radiusMiles?: number;
  includeNextSize?: boolean;
  shoeType?: ShoeType;
  minCondition?: BootCondition;
  maxRows?: number;
};

export type ListingInterestAdminRowDto = {
  interestId: number;
  listingId: number;

  interestedUserId: number;
  interestedEmail: string;
  interestedPostcode?: string | null;

  ownerUserId: number;
  ownerEmail: string;
  ownerPostcode?: string | null;

  sizeUk: number;
  shoeType: number; // byte on backend
  condition: number; // byte on backend
  notes?: string | null;

  status: number; // byte
  createdUtc: string;
};

/* ------------------------------------------------------------------
   MyStuff (Customer “inbox” style)
   ------------------------------------------------------------------ */

export type IncomingInterestDto = {
  interestId: number;
  listingId: number;
  createdUtc: string;

  interestedEmail: string;
  interestedPostcode?: string | null;

  sizeUk: number;
  shoeType: number;
  condition: number;
  notes?: string | null;
  photoUrl?: string | null;
};

// ✅ NEW: reply-link request (owner triggers an email to interested person)
export type CreateReplyLinkRequest = {
  ownerEmail: string;
};

// ✅ NEW: validate token response (reply page)
export type ValidateReplyTokenResponse = {
  ok: boolean;
  error?: string | null;

  interestId?: number | null;
  direction?: number | null;
  listingId?: number | null;
  listingSummary?: string | null;
};

// ✅ NEW: send reply request (reply page)
export type SendReplyRequest = {
  token: string;
  message: string;
};

const API_BASE =
  (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "") ||
  "https://localhost:7279";

function flattenValidationErrors(errorsObj: any): string | null {
  if (!errorsObj || typeof errorsObj !== "object") return null;

  // errors could be { field: ["msg1","msg2"], other: ["msg"] }
  const all: string[] = [];
  for (const key of Object.keys(errorsObj)) {
    const v = (errorsObj as any)[key];
    if (Array.isArray(v)) all.push(...v.map(String));
  }
  return all.length ? all.join(" ") : null;
}

async function parseError(res: Response): Promise<string> {
  try {
    const text = await res.text();
    if (!text) return `${res.status} ${res.statusText}`;

    // If it's JSON ProblemDetails / ValidationProblemDetails
    try {
      const j = JSON.parse(text);

      // ValidationProblemDetails: { title, status, errors: { field: [..] } }
      const flat = flattenValidationErrors(j?.errors);
      if (flat) return flat;

      // ProblemDetails: { title, detail }
      if (j?.title) return `${j.title}${j.detail ? `: ${j.detail}` : ""}`;

      // Fallback: stringify
      return typeof j === "string" ? j : JSON.stringify(j);
    } catch {
      // Not JSON
      return text;
    }
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });

  if (!res.ok) {
    const msg = await parseError(res);
    throw new Error(msg);
  }

  // 204 or empty response
  if (res.status === 204) return undefined as unknown as T;

  // Some endpoints might still return empty even with 200
  const txt = await res.text();
  if (!txt) return undefined as unknown as T;

  return JSON.parse(txt) as T;
}

/* ------------------------------------------------------------------
   Search Audit Dashboard (Admin)
   ------------------------------------------------------------------ */

/** Backend API DTOs (matches /api/admin/search-audit/dashboard) */
export type SearchAuditKpiApiDto = {
  totalSearches: number;
  zeroResultSearches: number;
  zeroResultRate: number; // 0..1
  avgDurationMs: number;
  p95DurationMs: number;
  avgResultCount: number;
  uniqueOutwardCodes: number;
};

export type SearchAuditDayRowApiDto = {
  dayUtc: string;
  searches: number;
  zeroResults: number;
  zeroResultRate: number; // 0..1
  avgDurationMs: number;
  p95DurationMs: number;
  avgResultCount: number;
};

export type SearchAuditTopSizeRowApiDto = {
  sizeUk: number;
  searches: number;
  zeroResults: number;
  zeroResultRate: number; // 0..1
  avgResultCount: number;
};

/** NEW: size-based supply gaps */
export type SearchAuditSupplyGapSizeRowApiDto = {
  sizeUk: number;
  zeroResultSearches: number;
  totalSearches: number;
  zeroRatePct: number; // 0..100 (already pct)
};

/** NEW: outward-based gaps */
export type SearchAuditOutwardGapRowApiDto = {
  outwardCode: string;
  searches: number;
  zeroResults: number;
  zeroResultRatePct: number; // 0..100 (already pct)
  avgRadiusMiles: number;
  mostCommonSizeUk?: number | null;
};

export type SearchAuditPerfBucketRowApiDto = {
  bucket: string;
  searches: number;
  percentOfTotal: number; // 0..1
};

export type SearchAuditLatestRowApiDto = {
  searchId: number;
  createdUtc: string;
  postcodeNoSpace: string | null;
  outwardCode: string | null;
  sizeUk: number | null;
  radiusMiles: number | null;
  includeNextSize: boolean;
  shoeType: number | null;
  minCondition: number | null;
  maxRows: number;
  resultCount: number;
  durationMs: number;
};

export type SearchAuditDashboardApiDto = {
  kpi: SearchAuditKpiApiDto;
  days: SearchAuditDayRowApiDto[];
  topSizes: SearchAuditTopSizeRowApiDto[];

  // ✅ NEW sets from backend
  supplyGapsBySize: SearchAuditSupplyGapSizeRowApiDto[];
  outwardGaps: SearchAuditOutwardGapRowApiDto[];

  perfBuckets: SearchAuditPerfBucketRowApiDto[];
  latest: SearchAuditLatestRowApiDto[];
};

/** Existing/legacy UI-facing types (kept to avoid refactoring UI right now) */
export type SearchAuditKpis = {
  totalSearches: number;
  distinctPostcodes: number; // not currently provided by backend (set to 0)
  zeroResultRatePct: number; // 0..100
  avgDurationMs: number;
  p95DurationMs?: number | null;
  windowStartUtc: string;
  windowEndUtc: string;
};

export type SearchAuditDayRow = {
  day: string;
  searches: number;
  zeroResults: number;
  avgDurationMs: number;
};

export type SearchAuditTopSizeRow = {
  sizeUk: number;
  searches: number;
  zeroResults: number;
  zeroRatePct: number; // 0..100
  avgResults: number;
};

export type SearchAuditOutwardRow = {
  outwardCode: string;
  searches: number;
  distinctPostcodes: number; // not currently provided by backend (set to 0)
  zeroResults: number;
  zeroRatePct: number; // 0..100
};

export type SearchAuditGapRow = {
  sizeUk: number;
  zeroResultSearches: number;
  totalSearches: number;
  zeroRatePct: number; // 0..100
};

export type SearchAuditPerfBucketRow = {
  bucket: string;
  searches: number;
};

export type SearchAuditLatestRow = {
  searchId: number;
  createdUtc: string;
  postcodeNoSpace: string;
  outwardCode?: number | string | null;
  sizeUk: number;
  radiusMiles: number;
  includeNextSize: boolean;
  shoeType?: number | null;
  minCondition?: number | null;
  maxRows: number;
  resultCount: number;
  durationMs: number;
};

export type SearchAuditDashboardDto = {
  kpis: SearchAuditKpis;
  byDay: SearchAuditDayRow[];
  topSizes: SearchAuditTopSizeRow[];
  topOutwardCodes: SearchAuditOutwardRow[];
  supplyGaps: SearchAuditGapRow[];
  perfBuckets: SearchAuditPerfBucketRow[];
  latest: SearchAuditLatestRow[];
};

export const bootloopClient = {
  // ---------------------------
  // MyStuff (customer)
  // ---------------------------

  // GET /api/my/incoming-interests?ownerEmail=me@x.com&daysBack=30&maxRows=200
  getMyIncomingInterests: (args: {
    ownerEmail: string;
    daysBack?: number;
    maxRows?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("ownerEmail", args.ownerEmail);
    qs.set("daysBack", String(args.daysBack ?? 30));
    qs.set("maxRows", String(args.maxRows ?? 200));

    return request<IncomingInterestDto[]>(
      `/api/my/incoming-interests?${qs.toString()}`,
      { method: "GET" }
    );
  },

  // ---------------------------
  // Reply flow (email relay)
  // ---------------------------

  // POST /api/interests/{interestId}/reply-link
  createReplyLink: (interestId: number, payload: CreateReplyLinkRequest) =>
    request<void>(
      `/api/interests/${encodeURIComponent(String(interestId))}/reply-link`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    ),

  // GET /api/reply/validate?t=TOKEN
  validateReplyToken: (token: string) =>
    request<ValidateReplyTokenResponse>(
      `/api/reply/validate?t=${encodeURIComponent(token)}`,
      { method: "GET" }
    ),

  // POST /api/reply/send
  sendReply: (payload: SendReplyRequest) =>
    request<void>(`/api/reply/send`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // ---------------------------
  // Admin: Search Audit Dashboard
  // GET /api/admin/search-audit/dashboard?daysBack=30&topN=10&maxRows=200&sizeUk=3.5&outward=IP9&shoeType=3&onlyZeroResults=false
  // ---------------------------
  getSearchAuditDashboard: (args?: {
    daysBack?: number;
    topN?: number;
    maxRows?: number;
    sizeUk?: number;
    outward?: string;
    shoeType?: number;
    onlyZeroResults?: boolean;
  }) => {
    const qs = new URLSearchParams();
    const daysBack = args?.daysBack ?? 30;

    qs.set("daysBack", String(daysBack));
    qs.set("topN", String(args?.topN ?? 10));
    qs.set("maxRows", String(args?.maxRows ?? 200));

    if (args?.sizeUk !== undefined && args?.sizeUk !== null)
      qs.set("sizeUk", String(args.sizeUk));

    if (args?.outward) qs.set("outward", args.outward.trim().toUpperCase());

    if (args?.shoeType !== undefined && args?.shoeType !== null)
      qs.set("shoeType", String(args.shoeType));

    qs.set("onlyZeroResults", String(args?.onlyZeroResults ?? false));

    return request<SearchAuditDashboardApiDto>(
      `/api/admin/search-audit/dashboard?${qs.toString()}`,
      { method: "GET" }
    ).then((api) => {
      // Map backend -> UI-facing SearchAuditDashboardDto (legacy shape)
      const windowEndUtc = new Date().toISOString();
      const windowStartUtc = new Date(
        Date.now() - daysBack * 86400000
      ).toISOString();

      const dto: SearchAuditDashboardDto = {
        kpis: {
          totalSearches: api.kpi.totalSearches,
          distinctPostcodes: 0, // not currently emitted by backend
          zeroResultRatePct: (api.kpi.zeroResultRate ?? 0) * 100,
          avgDurationMs: api.kpi.avgDurationMs,
          p95DurationMs: api.kpi.p95DurationMs,
          windowStartUtc,
          windowEndUtc,
        },

        byDay: (api.days ?? []).map((d) => ({
          day: d.dayUtc,
          searches: d.searches,
          zeroResults: d.zeroResults,
          avgDurationMs: d.avgDurationMs,
        })),

        topSizes: (api.topSizes ?? []).map((s) => ({
          sizeUk: s.sizeUk,
          searches: s.searches,
          zeroResults: s.zeroResults,
          zeroRatePct: (s.zeroResultRate ?? 0) * 100,
          avgResults: s.avgResultCount,
        })),

        // ✅ Correct: outward gaps from new outwardGaps set (already pct)
        topOutwardCodes: (api.outwardGaps ?? []).map((g) => ({
          outwardCode: g.outwardCode,
          searches: g.searches,
          distinctPostcodes: 0, // not currently emitted by backend
          zeroResults: g.zeroResults,
          zeroRatePct: g.zeroResultRatePct ?? 0,
        })),

        // ✅ Correct: supply gaps by size from new supplyGapsBySize set (already pct)
        supplyGaps: (api.supplyGapsBySize ?? []).map((g) => ({
          sizeUk: g.sizeUk,
          zeroResultSearches: g.zeroResultSearches,
          totalSearches: g.totalSearches,
          zeroRatePct: g.zeroRatePct ?? 0,
        })),

        perfBuckets: (api.perfBuckets ?? []).map((p) => ({
          bucket: p.bucket,
          searches: p.searches,
        })),

        latest: (api.latest ?? []).map((r) => ({
          searchId: r.searchId,
          createdUtc: r.createdUtc,
          postcodeNoSpace: r.postcodeNoSpace ?? "",
          outwardCode: r.outwardCode,
          sizeUk: r.sizeUk ?? 0,
          radiusMiles: r.radiusMiles ?? 0,
          includeNextSize: r.includeNextSize,
          shoeType: r.shoeType,
          minCondition: r.minCondition,
          maxRows: r.maxRows,
          resultCount: r.resultCount,
          durationMs: r.durationMs,
        })),
      };

      return dto;
    });
  },

  // ---------------------------
  // Listings
  // ---------------------------
  createListing: (payload: CreateListingRequest) =>
    request<BootListingDto>("/api/listings", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  searchListings: (args: { sizeUk?: number; shoeType?: ShoeType; maxRows?: number }) => {
    const qs = new URLSearchParams();
    if (args.sizeUk !== undefined && args.sizeUk !== null && args.sizeUk !== 0)
      qs.set("sizeUk", String(args.sizeUk));
    if (args.shoeType !== undefined && args.shoeType !== null)
      qs.set("shoeType", String(args.shoeType));
    qs.set("maxRows", String(args.maxRows ?? 50));

    return request<BootListingDto[]>(`/api/listings?${qs.toString()}`, {
      method: "GET",
    });
  },

  // ✅ Express interest
  // POST /api/interests
  createListingInterest: (listingId: number, payload: CreateInterestRequest) =>
    request<ListingInterestDto>(`/api/interests`, {
      method: "POST",
      body: JSON.stringify({
        listingId,
        email: payload.email,
        postcode:
          payload.postcode && payload.postcode.trim()
            ? payload.postcode
            : null,
      }),
    }),

  // ---------------------------
  // Matches
  // GET /api/matches?postcode=...&sizeUk=...&radiusMiles=...&includeNextSize=...&shoeType=...&minCondition=...&maxRows=...
  // ---------------------------
  findMatches: (args: FindMatchesArgs) => {
    const qs = new URLSearchParams();
    qs.set("postcode", args.postcode);
    qs.set("sizeUk", String(args.sizeUk));
    qs.set("radiusMiles", String(args.radiusMiles ?? 10));
    qs.set("includeNextSize", String(args.includeNextSize ?? true));
    if (args.shoeType !== undefined && args.shoeType !== null)
      qs.set("shoeType", String(args.shoeType));
    if (args.minCondition !== undefined && args.minCondition !== null)
      qs.set("minCondition", String(args.minCondition));
    qs.set("maxRows", String(args.maxRows ?? 50));

    return request<BootListingMatchDto[]>(`/api/matches?${qs.toString()}`, {
      method: "GET",
    });
  },

  // ---------------------------
  // Admin: Interests
  // GET /api/admin/interests?daysBack=30&maxRows=200
  // ---------------------------
  getAdminInterests: (args?: { daysBack?: number; maxRows?: number }) => {
    const qs = new URLSearchParams();
    qs.set("daysBack", String(args?.daysBack ?? 30));
    qs.set("maxRows", String(args?.maxRows ?? 200));
    return request<ListingInterestAdminRowDto[]>(
      `/api/admin/interests?${qs.toString()}`,
      { method: "GET" }
    );
  },

  // ---------------------------
  // Swap Preferences (optional)
  // ---------------------------
  createSwapPreference: (payload: SwapPreferenceCreateRequest) =>
    request<SwapPreferenceCreateResponse>("/api/swap-preferences", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // ---------------------------
  // Admin summary
  // ---------------------------
  getSwapPreferencesSummary: (args?: { daysBack?: number; topN?: number }) => {
    const qs = new URLSearchParams();
    qs.set("daysBack", String(args?.daysBack ?? 30));
    qs.set("topN", String(args?.topN ?? 10));
    return request<SwapPreferencesSummaryResponse>(
      `/api/admin/swap-preferences/summary?${qs.toString()}`,
      { method: "GET" }
    );
  },
};

// Small helpers for UI labels
export function shoeTypeLabel(v: ShoeType | number | null | undefined) {
  switch (v) {
    case 1:
      return "Firm ground (FG)";
    case 2:
      return "Soft ground (SG)";
    case 3:
      return "Astro/AG";
    case 4:
      return "Indoor";
    case 0:
      return "Unknown";
    default:
      return "Unknown";
  }
}

export function conditionLabel(v: BootCondition | number | null | undefined) {
  switch (v) {
    case 0:
      return "New";
    case 1:
      return "Like new";
    case 2:
      return "Good";
    case 3:
      return "Fair";
    case 4:
      return "Well used";
    default:
      return "Unknown";
  }
}